import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { formatCurrency } from '../../lib/formatters';

const PortfolioPerformanceMetrics = ({ user, filteredDeals, metrics }) => {
  // Broker/ISO Commission-focused view
  if (user?.roles?.includes('broker') || user?.roles?.includes('iso')) {
    const totalCommission = filteredDeals.reduce((sum, d) => {
      if (d.commission_rate && d.commission_type) {
        if (d.commission_type === 'iso') {
          return sum + (d.funding_amount * (d.commission_rate / 100));
        } else {
          const points = (d.rate - 1) * d.funding_amount;
          return sum + (points * (d.commission_rate / 100));
        }
      }
      return sum;
    }, 0);

    const avgCommission = filteredDeals.length > 0 ? totalCommission / filteredDeals.length : 0;
    
    const dealsWithCommission = filteredDeals.filter(d => d.commission_rate);
    const avgCommissionRate = dealsWithCommission.length > 0
      ? dealsWithCommission.reduce((sum, d) => sum + d.commission_rate, 0) / dealsWithCommission.length
      : 0;

    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Total Commission Earned</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {formatCurrency(totalCommission)}
            </div>
            <p className="text-xs text-gray-500">From {metrics.totalDeals} deals</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Average Commission per Deal</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">
              {formatCurrency(avgCommission)}
            </div>
            <p className="text-xs text-gray-500">Per funded deal</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Average Commission Rate</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-purple-600">
              {avgCommissionRate.toFixed(1)}%
            </div>
            <p className="text-xs text-gray-500">Across portfolio</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Admin/Funder view - traditional metrics
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-gray-600">Expected Payback</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold text-blue-600">{formatCurrency(metrics.totalPayback)}</div>
          <p className="text-xs text-gray-500">Total expected return</p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-gray-600">Net Cash Position</CardTitle>
        </CardHeader>
        <CardContent>
          <div className={`text-2xl font-bold ${metrics.totalProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            {formatCurrency(metrics.totalProfit)}
          </div>
          <p className="text-xs text-gray-500">Collected minus funded (to date)</p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-gray-600">Cash-on-Cash (to date)</CardTitle>
        </CardHeader>
        <CardContent>
          <div className={`text-2xl font-bold ${metrics.avgROI >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            {metrics.avgROI.toFixed(2)}%
          </div>
          <p className="text-xs text-gray-500">Net collected vs. funded</p>
        </CardContent>
      </Card>
    </div>
  );
};

export default PortfolioPerformanceMetrics;
